import * as http from "api/http";
import type { StripeService } from "@adfenix/types";

import { SERVICES_URL, handleError } from "api/helper";

const post = http.post;
const get = http.get;
const put = http.put;

const baseUrl = "stripe/auth/v1";
const baseUrlV2 = "stripe/auth/v2";

export const getStripeKeys =
  (): Promise<StripeService.CreateCardSetupSessionResponse> =>
    post(
      `${SERVICES_URL}/${baseUrl}/customer/create-credit-card-setup-session`
    ).catch(handleError);

export const getCardInfo = (): Promise<StripeService.CustomerCardDescription> =>
  get(`${SERVICES_URL}/${baseUrl}/customer/credit-card-description`).catch(
    handleError
  );

export const removeCard = (): Promise<void> =>
  post(`${SERVICES_URL}/${baseUrl}/customer/remove-credit-card`).catch(
    handleError
  );

export const addCard = (paymentMethodId: string): Promise<void> =>
  post(
    `${SERVICES_URL}/${baseUrl}/customer/save-payment-method/?PaymentMethodId=${paymentMethodId}`
  ).catch(handleError);

export const addStripeOfficePaymentMethod = ({
  subjectId,
  paymentMethodId,
  setAsActive
}: {
  subjectId: string;
  paymentMethodId: string;
  setAsActive: boolean;
}): Promise<void> =>
  post(`${SERVICES_URL}/${baseUrlV2}/officecustomer/add-payment-method`, {
    subjectId,
    paymentMethodId,
    setAsActive
  }).catch(handleError);

export const getOfficeStripePaymentMethods = (
  officeId: string
): Promise<StripeService.CustomerCardPaymentMethodsDescription[]> =>
  get(
    `${SERVICES_URL}/${baseUrlV2}/officecustomer/payment-methods/${officeId}`
  ).catch(handleError);

export const disableOfficeCreditCards = ({
  officeId
}: {
  officeId?: string;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${baseUrlV2}/officecustomer/payment-methods/clear-default/${officeId}`
  ).catch(handleError);

export const removeOfficeStripeCard = ({
  subjectId,
  paymentMethodId
}: {
  subjectId: string;
  paymentMethodId: string;
}): Promise<void> =>
  put(`${SERVICES_URL}/${baseUrlV2}/officecustomer/remove-payment-method`, {
    subjectId,
    paymentMethodId
  }).catch(handleError);

export const changeOfficeDefaultStripeCard = ({
  subjectId,
  paymentMethodId
}: {
  subjectId: string;
  paymentMethodId: string;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${baseUrlV2}/officecustomer/change-default-payment-method`,
    {
      subjectId,
      paymentMethodId
    }
  ).catch(handleError);

export const getOfficeCreditCardHistory = (
  officeId: string
): Promise<StripeService.CustomerTransactions[]> =>
  get(
    `${SERVICES_URL}/${baseUrlV2}/officecustomer/transactions/${officeId}`
  ).catch(handleError);

export const getHqStripePaymentMethods = (
  hqId: string
): Promise<StripeService.CustomerCardPaymentMethodsDescription[]> =>
  get(`${SERVICES_URL}/${baseUrlV2}/hqcustomer/payment-methods/${hqId}`).catch(
    handleError
  );

export const addStripeHqePaymentMethod = ({
  subjectId,
  paymentMethodId,
  setAsActive
}: {
  subjectId: string;
  paymentMethodId: string;
  setAsActive: boolean;
}): Promise<void> =>
  post(`${SERVICES_URL}/${baseUrlV2}/hqcustomer/add-payment-method`, {
    subjectId,
    paymentMethodId,
    setAsActive
  }).catch(handleError);

export const removeHqStripeCard = ({
  subjectId,
  paymentMethodId
}: {
  subjectId: string;
  paymentMethodId: string;
}): Promise<void> =>
  put(`${SERVICES_URL}/${baseUrlV2}/hqcustomer/remove-payment-method`, {
    subjectId,
    paymentMethodId
  }).catch(handleError);

export const disableHqCreditCards = ({
  hqId
}: {
  hqId?: string;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${baseUrlV2}/hqcustomer/payment-methods/clear-default/${hqId}`
  ).catch(handleError);

export const changeHqDefaultStripeCard = ({
  subjectId,
  paymentMethodId
}: {
  subjectId: string;
  paymentMethodId: string;
}): Promise<void> =>
  put(`${SERVICES_URL}/${baseUrlV2}/hqcustomer/change-default-payment-method`, {
    subjectId,
    paymentMethodId
  }).catch(handleError);

export const getHqCreditCardHistory = (
  hqId: string
): Promise<StripeService.CustomerTransactions[]> =>
  get(`${SERVICES_URL}/${baseUrlV2}/hqcustomer/transactions/${hqId}`).catch(
    handleError
  );

export const getCheckCreditCardValidityHq = (
  hqId: string
): Promise<StripeService.PaymentMethodCheckResponse> =>
  post(
    `${SERVICES_URL}/${baseUrlV2}/hqcustomer/payment-methods/check/${hqId}`
  ).catch(handleError);

export const getCheckCreditCardValidityOffice = (
  officeId: string
): Promise<StripeService.PaymentMethodCheckResponse> =>
  post(
    `${SERVICES_URL}/${baseUrlV2}/officecustomer/payment-methods/check/${officeId}`
  ).catch(handleError);

export const getStripeHqTransactionGroups = (
  subjectId: string
): Promise<StripeService.GroupedInvoices[]> =>
  get(
    `${SERVICES_URL}/${baseUrlV2}/hqcustomer/transactions/${subjectId}/group`
  ).catch(handleError);

export const getStripeOfficeTransactionGroups = (
  subjectId: string
): Promise<StripeService.GroupedInvoices[]> =>
  get(
    `${SERVICES_URL}/${baseUrlV2}/officecustomer/transactions/${subjectId}/group`
  ).catch(handleError);
